import { Trans, useTranslation } from 'next-i18next'
import { useContext, useState, useEffect } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'

import { Modal, Props as ModalProps } from './Modal'
import { CurrencyType, EventHookInterface } from '../../types'
import { CTA } from '../CTA'
import { H3, H5, H4, P } from '../Headings'
import { StripeContext } from '../../context'
import { EmailRsvpHock } from '../../hooks/emailRsvp'
import { StripeCardPurchaseFormComponent } from '../Forms/StripeCardPurchaseFormComponent'
import { PurchaseDiv, RefundDiv } from './processPaymentModalStyles'

// Props.
interface Props extends ModalProps {
  eventHook: EventHookInterface
  emailRsvpHock: EmailRsvpHock
  closeModal: (showPaymentSuccess: boolean) => void
}

// Payment Process Modal.
export const EmailRsvpProcessPaymentModal: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  // Props.
  const { eventHook, closeModal, emailRsvpHock } = props
  const { goToPayEventRegisterUser, email, name } = emailRsvpHock
  const { value } = eventHook

  const { expressCreatePaymentIntent } = useContext(StripeContext)
  // const { currentUser } = useContext(UserContext)

  const [alreadyGoing, setAlreadyGoing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [refundError, setRefundError] = useState('')
  const [allowToRefund, setAllowToRefund] = useState(false)

  const stripe = useStripe()
  const elements = useElements()

  const purchase = async () => {
    setError('')

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    // if (!currentUser.full_name) {
    //   setError('Please update your name in settings page')
    //   return
    // }

    setLoading(true)
    // analyticEvent.fbAddPaymentInfo()
    let purchaseSuccess = false

    try {
      const intentResult = await expressCreatePaymentIntent({
        event_id: value.id,
        group_id: value.hosted_group.id,
        email: email,
      }).catch((error) => {
        console.error(error)
        setError(
          t(
            'Error occurs while processing your request',
            'Error occurs while processing your request',
          ),
        )
      })

      if (intentResult) {
        const { client_secret } = intentResult

        const result = await stripe.confirmCardPayment(client_secret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: name,
            },
          },
        })

        if (result.error) {
          setError(result.error.message)
        } else {
          // The payment has been processed!
          if (result.paymentIntent.status === 'succeeded') {
            await goToPayEventRegisterUser()
              .then(() => {
                purchaseSuccess = true
              })
              .catch((error) => {
                console.error(error)
                setError(
                  t(
                    'Error occurs while processing your purchase',
                    'Error occurs while processing your purchase',
                  ),
                )
              })
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
      if (purchaseSuccess) {
        closeModal(true)
      }
    }
  }

  const refund = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleChange = () => {
    if (error) {
      setError('')
    }
  }

  const paymentCost = value.currency === CurrencyType.IDR ? '5%' : '2.9% + 50¢'

  return (
    <Modal close={() => closeModal(false)}>
      {!alreadyGoing ? (
        <PurchaseDiv>
          <StripeCardPurchaseFormComponent
            error={error}
            onClose={closeModal}
            purchase={purchase}
            loading={loading}
            onCardDetailsChanged={handleChange}
            event={value}
          />
        </PurchaseDiv>
      ) : (
        <RefundDiv>
          {/* Header. */}
          <H3 className="header">
            {t(
              `EmailRsvpProcessPaymentModal-375968220`,
              `Cancel my registration to this event`,
            )}
          </H3>
          {allowToRefund && (
            <H5 className="content">
              <Trans i18nKey="ProcessPaymentModal-1178378214">
                By cancelling your registered attendance to this event more than
                24 hours out from the event date you are elligable for a refund.
                This is a full refund minus the original transaction cost of{' '}
                {{ paymentCost }}.
              </Trans>
            </H5>
          )}
          {!allowToRefund && (
            <H5 className="content">
              {t(
                `EmailRsvpProcessPaymentModal-2017410455`,
                `Cancelling your registered attendance less than 24 hours out from the event date is a non-refundable action. You will not be reimbursed for the cost of your ticket.`,
              )}
            </H5>
          )}
          {/* Refund Button. */}
          <div className="refundButtonContainer">
            <CTA
              className="red button refundButton"
              onClick={refund}
              disabled={loading}>
              {t(`Confirm cancelation`, `Confirm cancelation`)}
            </CTA>
            {refundError && <P className="detail refundError">{refundError}</P>}
          </div>
          {/* Back Button. */}
          <CTA className="button closeButton" onClick={() => closeModal(false)}>
            {t(`Go back`, `Go back`)}
          </CTA>
        </RefundDiv>
      )}
    </Modal>
  )
}
